import cn from 'classnames';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';

import {
  addAskDownvote,
  addAskUpvote,
  askAuthRedirect,
  reportAskItem,
  setAnsweringAskQuestion,
  setAskAnswersCollapsed,
  setAskQuestionsCollapsed,
  setViewingAskQuestionsDesktop,
  submitAskItem
} from 'actions/ask';
import AnswerList from 'components/productdetail/ask/AnswerList';
import ItemForm from 'components/productdetail/ask/ItemForm';
import VoteBox from 'components/productdetail/ask/VoteBox';
import { questionPropType } from 'components/productdetail/ask/commonAskPropTypes';
import { SmallLoader } from 'components/Loader';
import { trackEvent } from 'helpers/analytics';
import {
  formatAskAnswerTrackEventParams,
  formatAskQuestionTrackEventParams,
  formatAskTrackEventParams
} from 'helpers/AskUtils';
import { parseTextToReact } from 'helpers/HtmlHelpers';

import css from 'styles/containers/ask.scss';

export class Ask extends Component {
  onReportAskQuestion = key => {
    const { reportAskItem, params: { productId } } = this.props;
    trackEvent('TE_ASK_REPORT_QUESTION', formatAskQuestionTrackEventParams(productId, key));
    reportAskItem(productId, key);
  };

  onReportAskAnswer = key => {
    const { reportAskItem, params: { productId } } = this.props;
    trackEvent('TE_ASK_REPORT_ANSWER', formatAskAnswerTrackEventParams(productId, key));
    reportAskItem(productId, key);
  };

  onAddAskQuestionUpvote = key => {
    const { addAskUpvote, params: { productId, colorId } } = this.props;
    trackEvent('TE_ASK_UPVOTE_QUESTION', formatAskQuestionTrackEventParams(productId, key));
    addAskUpvote(productId, colorId, key);
  };

  onAddAskAnswerUpvote = key => {
    const { addAskUpvote, params: { productId, colorId } } = this.props;
    trackEvent('TE_ASK_UPVOTE_ANSWER', formatAskAnswerTrackEventParams(productId, key));
    addAskUpvote(productId, colorId, key);
  };

  onAddAskQuestionDownvote = key => {
    const { addAskDownvote, params: { productId, colorId } } = this.props;
    trackEvent('TE_ASK_DOWNVOTE_QUESTION', formatAskQuestionTrackEventParams(productId, key));
    addAskDownvote(productId, colorId, key);
  };

  onAddAskAnswerDownvote = key => {
    const { addAskDownvote, params: { productId, colorId } } = this.props;
    trackEvent('TE_ASK_DOWNVOTE_ANSWER', formatAskAnswerTrackEventParams(productId, key));
    addAskDownvote(productId, colorId, key);
  };

  onAskAuthRedirect = () => {
    const { askAuthRedirect, params: { productId, colorId } } = this.props;
    askAuthRedirect(productId, colorId);
  };

  onSetAnsweringAskQuestion = (key, answering) => {
    const {
      params: { productId },
      setAnsweringAskQuestion
    } = this.props;
    const event = answering ? 'TE_ASK_VIEW_ANSWER_FORM' : 'TE_ASK_LEAVE_ANSWER_FORM';
    trackEvent(event, formatAskQuestionTrackEventParams(productId, key));
    setAnsweringAskQuestion(key, answering);
  };

  toggleAskQuestionsCollapsed = () => {
    const {
      data: { questionsCollapsed },
      params: { productId },
      setAskQuestionsCollapsed
    } = this.props;
    const event = questionsCollapsed ? 'TE_ASK_EXPAND_QUESTIONS' : 'TE_ASK_COLLAPSE_QUESTIONS';
    trackEvent(event, formatAskTrackEventParams(productId));
    setAskQuestionsCollapsed(!questionsCollapsed);
  };

  onSetAskAnswersCollapsed = (questionKey, collapsed) => {
    const {
      params: { productId },
      setAskAnswersCollapsed
    } = this.props;
    const event = collapsed ? 'TE_ASK_COLLAPSE_ANSWERS' : 'TE_ASK_EXPAND_ANSWERS';
    trackEvent(event, formatAskQuestionTrackEventParams(productId, questionKey));
    setAskAnswersCollapsed(questionKey, collapsed);
  };

  toggleViewingAskQuestions = () => {
    const {
      props: {
        data: { viewingQuestionsDesktop: viewingQuestions },
        params: { productId },
        setViewingAskQuestionsDesktop
      }
    } = this;
    const event = viewingQuestions ? 'TE_ASK_VIEW_QUESTION_FORM' : 'TE_ASK_LEAVE_QUESTION_FORM';
    trackEvent(event, formatAskTrackEventParams(productId));
    setViewingAskQuestionsDesktop(!viewingQuestions);
  };

  onSubmitAskItem = (parentKey, text) => {
    const { submitAskItem, params: { productId, colorId } } = this.props;

    let event;
    const eventParams = parentKey
      ? formatAskQuestionTrackEventParams(productId, parentKey)
      : formatAskTrackEventParams(productId);

    if (text) {
      event = parentKey ? 'TE_ASK_SUBMIT_ANSWER' : 'TE_ASK_SUBMIT_QUESTION';
      submitAskItem(productId, colorId, parentKey, text);
    } else {
      event = parentKey ? 'TE_ASK_SUBMIT_EMPTY_ANSWER' : 'TE_ASK_SUBMIT_EMPTY_QUESTION';
    }

    trackEvent(event, eventParams);
  };

  makeQuestionMarkup = question => {
    const { key, text } = question;
    const {
      props: { data: { authenticated } },
      onAskAuthRedirect,
      onAddAskAnswerDownvote,
      onAddAskAnswerUpvote,
      onAddAskQuestionDownvote,
      onAddAskQuestionUpvote,
      onReportAskAnswer,
      onReportAskQuestion,
      onSetAnsweringAskQuestion,
      onSetAskAnswersCollapsed,
      onSubmitAskItem
    } = this;
    return (
      <div key={key} className={css.question}>
        <div className={css.questionHeading}>Question</div>
        <p className={css.questionText}>
          {parseTextToReact(text)}
        </p>
        <VoteBox
          item={question}
          isQuestion={true}
          onAddAskDownvote={onAddAskQuestionDownvote}
          onAddAskUpvote={onAddAskQuestionUpvote}
          onReportAskItem={onReportAskQuestion} />
        <div className={css.answerListForQuestion}>
          <AnswerList
            authenticated={authenticated}
            question={question}
            onAddAskDownvote={onAddAskAnswerDownvote}
            onAddAskUpvote={onAddAskAnswerUpvote}
            onReportAskAnswer={onReportAskAnswer}
            onSetAnsweringAskQuestion={onSetAnsweringAskQuestion}
            onSetAskAnswersCollapsed={onSetAskAnswersCollapsed}
            onAskAuthRedirect={onAskAuthRedirect}
            onSubmitAskItem={onSubmitAskItem}/>
        </div>
        <hr />
      </div>
    );
  };

  render() {
    const {
      props: {
        data: {
          questionsCollapsed: collapsed,
          viewingQuestionsDesktop: viewingQuestions,
          askError, authenticated, loading, questions
        }
      },
      toggleAskQuestionsCollapsed,
      toggleViewingAskQuestions
    } = this;

    if (askError) {
      return null;
    }

    const itemFormProps = {
      questionKey: null,
      textareaPlaceholder: 'Ask the Zappos Community',
      authenticated,
      onSetAnsweringAskQuestion: this.onSetAnsweringAskQuestion,
      onAskAuthRedirect: this.onAskAuthRedirect,
      onSubmitAskItem: this.onSubmitAskItem
    };

    let askAQuestionButton;
    let askBody = null;

    if (loading) {
      askBody = <SmallLoader />;
    } else {
      if (questions.length > 0) {
        const text = viewingQuestions ? 'Ask a Question' : 'View Questions';
        askAQuestionButton = <button type="button" className={css.askAQuestionButton} onClick={toggleViewingAskQuestions}>{text}</button>;
        const questionsMarkup = questions.map(question => this.makeQuestionMarkup(question));
        let collapser = null, collapsibleMarkup = null;
        if (questions.length > 1) {
          collapsibleMarkup = collapsed ? null : <div>{questionsMarkup.slice(1)}</div>;
          const seeOrHide = collapsed ? 'See' : 'Hide';
          const optionalCount = collapsed ? ` ${questions.length}` : null;
          collapser = (
            <button type="button" className={css.questionsCollapser} onClick={toggleAskQuestionsCollapsed}>
              {seeOrHide} All{optionalCount} Customer Questions
            </button>
          );
        }
        askBody = (
          <div className={css.body}>
            {questionsMarkup[0]}
            {collapsibleMarkup}
            {collapser}
          </div>
        );
      }
    }

    let askAQuestionForm = null;
    if (!loading) {
      askAQuestionForm = (
        <div className={css.askQuestionForm}>
          <ItemForm {...itemFormProps} />
        </div>
      );
    }

    return (
      <div id="questions" className={cn(css.ask, { [css.viewingQuestions]: viewingQuestions && questions.length > 0 })}>
        <h2 className={css.banner}>Customer Questions</h2>
        {askAQuestionButton}
        {askAQuestionForm}
        {askBody}
      </div>
    );
  }
}

Ask.propTypes = {
  params: PropTypes.shape({
    productId: PropTypes.string.isRequired,
    colorId: PropTypes.string
  }).isRequired,

  data: PropTypes.oneOfType([
    PropTypes.shape({ loading: PropTypes.bool.isRequired }).isRequired,

    PropTypes.shape({
      questions: PropTypes.arrayOf(questionPropType).isRequired,
      questionsCollapsed: PropTypes.bool.isRequired,
      viewingQuestionsDesktop: PropTypes.bool.isRequired,
      authenticated: PropTypes.bool.isRequired
    }).isRequired
  ]).isRequired,

  addAskUpvote: PropTypes.func.isRequired,
  addAskDownvote: PropTypes.func.isRequired,
  askAuthRedirect: PropTypes.func.isRequired,
  reportAskItem: PropTypes.func.isRequired,
  setAnsweringAskQuestion: PropTypes.func.isRequired,
  setAskQuestionsCollapsed: PropTypes.func.isRequired,
  setAskAnswersCollapsed: PropTypes.func.isRequired,
  setViewingAskQuestionsDesktop: PropTypes.func.isRequired,
  submitAskItem: PropTypes.func.isRequired
};

export function mapStateToProps(state) {
  return { data: state.ask };
}

export const mapDispatchToProps = {
  addAskUpvote,
  addAskDownvote,
  askAuthRedirect,
  reportAskItem,
  setAnsweringAskQuestion,
  setAskQuestionsCollapsed,
  setAskAnswersCollapsed,
  setViewingAskQuestionsDesktop,
  submitAskItem
};

export default connect(mapStateToProps, mapDispatchToProps)(Ask);
